import { List } from '@kerfjs/ui/list';
import { Text } from '@kerfjs/ui/text';
import { ValueTable, ValueTableRow } from '@kerfjs/ui/value-table';
import type { SafeHtml } from 'kerfjs';

import type { AnalysisReport } from '../../../data/api.js';
import { me } from '../../../data/state.js';
import { supplierLeads } from '../../../lib/supplier-leads.js';
import { CurrencyAmount } from '../../currency-amount.js';
import { SupplierLeadList } from '../../supplier-lead-list.js';

export function EvidenceBenchmarks({
  report,
  lineVerdictId,
  base,
}: {
  report: AnalysisReport;
  lineVerdictId: string;
  base: string;
}): SafeHtml {
  const benchmarks = report.benchmarks.filter((row) => row.lineVerdictId === lineVerdictId);
  const leads = supplierLeads(report.benchmarks, report.verdicts).filter((lead) =>
    benchmarks.some((benchmark) => benchmark.id === lead.benchmarkId),
  );
  const canAdd = me.value?.roles.some((role) => role === 'reviewer' || role === 'admin') === true;
  const estimates = new Map(
    report.estimates
      .filter((row) => row.lineVerdictId === lineVerdictId && row.benchmarkId !== null)
      .map((row) => [row.benchmarkId as string, row]),
  );
  return (
    <List gap="m">
      {leads.length > 0 ? (
        <section data-line-supplier-leads>
          <List gap="xs">
            <Text variant="h3">Where to source</Text>
            <SupplierLeadList leads={leads} canAdd={canAdd} />
          </List>
        </section>
      ) : null}
      {benchmarks.length === 0 ? (
        <Text tone="quiet">No benchmarks recorded.</Text>
      ) : (
        benchmarks.map((benchmark) => {
          const estimate = estimates.get(benchmark.id);
          return (
            <div class="evidence-benchmark" data-key={benchmark.id}>
              <List gap="xs">
                <Text>
                  <strong>
                    {benchmark.sourceUrl !== null ? (
                      <a href={benchmark.sourceUrl} target="_blank" rel="noopener noreferrer">
                        {benchmark.sourceTitle ?? 'Source'}
                      </a>
                    ) : (
                      (benchmark.sourceTitle ?? 'Manual benchmark')
                    )}
                  </strong>
                </Text>
                <ValueTable label="Benchmark">
                  <ValueTableRow
                    label="Price"
                    value={
                      benchmark.price === null ? (
                        '—'
                      ) : (
                        <CurrencyAmount value={Number(benchmark.price)} currency={benchmark.currency ?? base} />
                      )
                    }
                  />
                  <ValueTableRow label="Geography" value={benchmark.geography ?? 'No geography'} />
                  <ValueTableRow label="Basis" value={benchmark.basis ?? 'No basis'} />
                  <ValueTableRow label="Match" value={benchmark.matchQuality ?? 'unrated match'} />
                </ValueTable>
                {benchmark.notes !== null && benchmark.notes !== '' ? (
                  <Text tone="quiet" size="compact">
                    {benchmark.notes}
                  </Text>
                ) : null}
                <Text>
                  {estimate?.landedUnitPrice == null ? (
                    'No landed estimate'
                  ) : (
                    <>
                      Landed:{' '}
                      <CurrencyAmount value={Number(estimate.landedUnitPrice)} currency={estimate.currency ?? base} />
                    </>
                  )}
                </Text>
                {(estimate?.breakdown?.lines?.length ?? 0) > 0 ? (
                  <details class="evidence-breakdown">
                    <summary>Landed-cost breakdown</summary>
                    <ValueTable label="Landed-Cost Breakdown">
                      {estimate?.breakdown?.lines?.map((item) => (
                        <ValueTableRow
                          label={item.label}
                          value={<CurrencyAmount value={item.amount} currency={estimate.currency ?? base} />}
                        />
                      ))}
                    </ValueTable>
                  </details>
                ) : null}
              </List>
            </div>
          );
        })
      )}
    </List>
  );
}
